import { Type } from '@sinclair/typebox';
// Canonical RFC-4122 UUID. Used for the asserted actor — the read endpoint
// scopes self-reads on `actor_user_id = caller.id`, so a track-asserted user
// id must be the hub user UUID to be self-readable by that human.
const UUID_PATTERN = '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
/** The human who performed the action, as asserted by the calling track.
 *  The track's API key is the verified provenance; this identity is trusted. */
export const AuditEventActorSchema = Type.Object({
    userId: Type.Optional(Type.String({ pattern: UUID_PATTERN })),
    email: Type.Optional(Type.String({ maxLength: 320 })),
    roles: Type.Optional(Type.Array(Type.String({ maxLength: 128 }))),
});
/** Reporting channel of an audit row. The hub stamps this — `hub_api` for its
 *  own onResponse capture, `track` for events ingested via POST /audit/events,
 *  `extension` for an in-process extension's out-of-request event written
 *  through `ctx.audit` (HUB-112). */
export const AuditSourceSchema = Type.Union([
    Type.Literal('hub_api'),
    Type.Literal('track'),
    Type.Literal('extension'),
]);
/** The thing acted on, e.g. `{ type: 'estimate', id: 'est_123' }`. */
export const AuditEventResourceSchema = Type.Object({
    type: Type.String({ minLength: 1, maxLength: 128 }),
    id: Type.Optional(Type.String({ maxLength: 256 })),
});
/** Free-form event detail. Redacted (secrets stripped) and folded into the
 *  payload hash hub-side; never store raw credentials here. */
export const AuditEventMetadataSchema = Type.Record(Type.String(), Type.Unknown());
/**
 * Write payload for `POST /audit/events` — an externally-reported audit event.
 * Deliberately small: a verb (`action`), the `resource`, the acting `actor`,
 * and `metadata`. The HTTP fields are optional — a track-server middleware
 * fills them from the request/response it observed; manual business-event
 * calls usually omit them.
 */
export const AuditEventBodySchema = Type.Object({
    action: Type.String({ minLength: 1, maxLength: 128 }),
    /** The reporting track's id (the hub records it as the `track` source). */
    trackId: Type.Optional(Type.String({ pattern: UUID_PATTERN })),
    resource: Type.Optional(AuditEventResourceSchema),
    actor: Type.Optional(AuditEventActorSchema),
    status: Type.Optional(Type.Integer({ minimum: 100, maximum: 599 })),
    method: Type.Optional(Type.String({ maxLength: 16 })),
    route: Type.Optional(Type.String({ maxLength: 512 })),
    path: Type.Optional(Type.String({ maxLength: 1024 })),
    durationMs: Type.Optional(Type.Integer({ minimum: 0 })),
    sessionId: Type.Optional(Type.String({ maxLength: 256 })),
    metadata: Type.Optional(AuditEventMetadataSchema),
});
/** Read filters for `GET /audit/events`. */
export const AuditEventListQuerySchema = Type.Object({
    source: Type.Optional(AuditSourceSchema),
    trackId: Type.Optional(Type.String({ pattern: UUID_PATTERN })),
    actorUserId: Type.Optional(Type.String({ pattern: UUID_PATTERN })),
    route: Type.Optional(Type.String({ maxLength: 512 })),
    method: Type.Optional(Type.String({ maxLength: 16 })),
    status: Type.Optional(Type.Integer({ minimum: 100, maximum: 599 })),
    from: Type.Optional(Type.String({ maxLength: 64 })),
    to: Type.Optional(Type.String({ maxLength: 64 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
const Nullable = (schema) => Type.Union([schema, Type.Null()]);
/** One audit row as returned by the read endpoint. The body snapshot is
 *  already redacted at write time, so it is safe to surface here. */
export const AuditEventViewSchema = Type.Object({
    id: Type.String(),
    createdAt: Type.String(),
    source: Type.String(),
    trackId: Nullable(Type.String()),
    actorType: Type.String(),
    actorUserId: Nullable(Type.String()),
    actorEmail: Nullable(Type.String()),
    actorRoles: Type.Union([Type.Array(Type.String()), Type.Null()]),
    actorApiKeyId: Nullable(Type.String()),
    actorApiKeyPrefix: Nullable(Type.String()),
    actorIp: Nullable(Type.String()),
    actorUserAgent: Nullable(Type.String()),
    httpMethod: Type.String(),
    httpRoute: Type.String(),
    httpPath: Type.String(),
    httpStatus: Type.Integer(),
    durationMs: Type.Union([Type.Integer(), Type.Null()]),
    payloadHash: Type.String(),
    body: Type.Optional(Type.Unknown()),
    bodyTruncated: Type.Boolean(),
    sessionId: Nullable(Type.String()),
    traceId: Nullable(Type.String()),
    spanId: Nullable(Type.String()),
});
export const AuditEventListResponseSchema = Type.Object({
    events: Type.Array(AuditEventViewSchema),
    total: Type.Integer(),
    limit: Type.Integer(),
    offset: Type.Integer(),
});
//# sourceMappingURL=audit.js.map